import { Injectable } from "@angular/core"
import { ComponentStore, tapResponse } from "@ngrx/component-store"
import { uniqBy } from "lodash"
import { Observable, of } from "rxjs"
import { map, switchMap, tap, withLatestFrom } from "rxjs/operators"
import { CampaignDetailsRepository } from "src/app/shared/data-access/campaign-details/campaign-details.repository"
import { CampaignListFetchStore } from "src/app/shared/data-access/campaign-list/campaign-list.store"
import { UniAuthFacade } from "unifonic-spa-common/src"
import { CampaignListRepository } from "../../data-access/campaign-list/campaign-list.repository"
import { getVariablesForSearchByPhrase } from "./campaignid-selector.utils"
import { Channels } from "../../store-mocks/channels.store"


type State = {
    label: string
    accountId: string
    channels: string
    config: any
    defaultEntities: any[]
    defaultLoading: boolean
    defaultValue: string
    entities: any[]
    limit: number
}

const initialState: State = {
  label: "",
  accountId: '',
  channels: Channels.ALL,
  config: {
    noResultMessage: 'ci.common.noResults',
    tooManyItemsMessage: 'ci.common.tooManyItems',
    placeholder: 'ci.common.campaignName',
    debounceTime: 800,
    minPhraseLength: 1
  },
  defaultEntities: [],
  defaultLoading: false,
  defaultValue: '',
  entities: [],
  limit: 30,
}


@Injectable()
export class CampaignIdSelectorStore extends ComponentStore<State>{

    constructor(
        private campaignListFetch: CampaignListFetchStore,
        private repository: CampaignDetailsRepository,
        private campaignListRepository: CampaignListRepository,
        private authFacade: UniAuthFacade
    ){
        super(initialState)
    }

    readonly label$ = this.select(state => state.label)
    readonly accountId$ = this.select(state => state.accountId)
    readonly channels$ = this.select(state => state.channels)
    readonly config$ = this.select(state => state.config)
    readonly defaultEntities$ = this.select(state => state.defaultEntities)
    readonly defaultLoading$ = this.select(state => state.defaultLoading)
    readonly defaultValue$ = this.select(state => state.defaultValue)
    readonly entities$ = this.select(state => state.entities)
    readonly limit$ = this.select(state => state.limit)


    //selectors
    timezone$ = this.authFacade.userMe$.pipe(
        map((user: any) => user.user.timezone)
    )

    readonly campaigns$ = this.select(
        this.defaultEntities$,
        this.entities$,
        (defaultEntities, entities) => uniqBy([...entities, ...defaultEntities], 'value')
    )

    readonly loading$ = this.campaignListFetch.loading$

    vm$ = this.select(
        this.config$,
        this.accountId$,
        this.campaigns$,
        this.defaultLoading$,
        this.label$,
        this.defaultValue$,
        (
            config,
            accountId,
            campaigns,
            campaignsLoading,
            label,
            defaultValue,
        ) => {
            const selectedItem = campaigns.find((c) => c.value === defaultValue)
            return {
                config,
                accountId,
                campaigns,
                campaignsLoading,
                label,
                defaultLabel: selectedItem?.label || '',
                maxSize: 150
            }
        }
    )

    //Updaters
    readonly mergeOptions = this.updater((state, options: any[]) => {
        const uniqueOptions = options.filter((o) => {
            return !state.defaultEntities.find(de => de.value === o.value)
        })

        return {...state, defaultEntities: [ ...state.defaultEntities, ...uniqueOptions]}
    })

    readonly fetchCampaignsByPhrase = this.effect((phrase$: Observable<string>) => {
        return phrase$.pipe(
            tap((phrase) => {
                this.patchState({defaultLoading: true})
            }),
            withLatestFrom(
                this.accountId$,
                this.channels$,
                this.timezone$
            ),
            switchMap(([campaignNamePhrase, accountId, channels, timezone]) => this.campaignListRepository.gqlQueryCampaignList(
                {
                    ...getVariablesForSearchByPhrase(campaignNamePhrase, timezone, accountId),
                    channel: channels
                },
                `campaignName, campaignId, channel`
            ).pipe(
                tapResponse(
                    (response) => {
                        const entities = response.entries.map(e => ({
                            label: e.campaignName,
                            value: e.campaignId,
                            data: {
                                channel: e.channel
                            }
                        }))
                        this.patchState({defaultLoading: false, entities })
                    }, () => {}
                )
            ))
        )
    })

    readonly accountIdChange = this.effect(() => {
        return this.accountId$.pipe(
            tap((accountId: string) => {
                if(!accountId.length){
                    this.patchState({label: ''})
                }
            })
        )
    })

    readonly onFormValueChange = this.effect((event$: Observable<any>) => {
        return event$.pipe(
            tap((event:any) => {
                if(event.label !== ''){
                    this.mergeOptions([event])
                }
                this.patchState({label: event.value})
            })
        )
    })

    readonly fetchLatest = this.effect((trigger$: Observable<void>) => {
        return trigger$.pipe(
            tap(() => {
                this.patchState({defaultLoading: true})
            }),
            withLatestFrom(this.accountId$, this.channels$),
            switchMap(([_, accountId, channels]) => this.campaignListRepository.gqlQueryCampaignList(
                    {accountId, limit: 10, channel: channels},
                    `campaignName, campaignId, channel`
                ).pipe(
                    map((response) => {
                        return response.entries.map(e => ({
                            label: e.campaignName,
                            value: e.campaignId,
                            data: {
                                channel: e.channel
                            }
                        }))
                    }),
                    tapResponse(
                        (entities) => {
                            this.patchState({defaultEntities: entities})
                        },() => {}
                    ),
                    map(() => accountId)
                )
            ),
            withLatestFrom(
                this.defaultValue$,
                this.defaultEntities$.pipe(map((entities) => entities.map(e => e.value)))
            ),
            switchMap(([accountId, value, defaultValues]) => {
                if(!value || defaultValues.indexOf(value) !== -1){
                    this.patchState({defaultLoading: false})
                    return of([])
                }
                return this.campaignListRepository.gqlQueryCampaignList(
                    {accountId, limit: 1, campaignId: value},
                    `campaignName, campaignId, channel`
                ).pipe(
                    map(response => response.entries[0] || {}),
                    tapResponse(
                        (e: any) => {
                            this.mergeOptions([{
                                label: e.campaignName,
                                value: e.campaignId,
                                data: {
                                    channel: e.channel
                                }
                            }])
                            this.patchState({defaultLoading: false})
                        }, () => {}
                    )
                )
            })

        )
    })
}


